/**
 * Images in the composer: files pasted in, and the one line said when the
 * model will not see what is attached. Pure, so they are tested.
 */

/**
 * The files a paste carries, when it carries no text. With text the text
 * wins: a copy from Word or a web page carries a picture of itself beside it.
 */
export function pastedFiles(data: Pick<DataTransfer, 'getData' | 'files'> | null): File[] {
  if (!data || data.getData('text/plain')) return [];
  return Array.from(data.files ?? []);
}

/** What the Worker sends a model that sees: these as they are, HEIC and AVIF
 *  converted first (worker/lib/engine/images.ts, `modelImage`). */
const SEEN = /^(png|jpe?g|webp|gif|heic|heif|avif)$/;

const extension = (f: { name: string; mime: string }): string => {
  const fromName = /\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase();
  return fromName ?? f.mime.replace(/^image\//, '').toLowerCase();
};

const isImage = (f: { name: string; mime: string }): boolean =>
  (/^image\//.test(f.mime) && f.mime !== 'image/svg+xml') || /\.(png|jpe?g|webp|gif|heic|heif|avif|bmp|tiff?)$/i.test(f.name);

/**
 * The line under the attachments when an image will not reach the model: the
 * model cannot see, with the model menu offered; or the image's type cannot
 * be opened. Null when there is nothing to say.
 */
export function imageNotice(
  files: ReadonlyArray<{ name: string; mime: string }>,
  model: { short: string; vision: boolean } | undefined
): { text: string; switchModel: boolean } | null {
  const images = files.filter(isImage);
  if (!images.length || !model) return null;
  if (!model.vision) return { text: `${model.short} cannot see images.`, switchModel: true };
  const unread = images.find((f) => !SEEN.test(extension(f)));
  return unread ? { text: `${extension(unread).toUpperCase()} images cannot be opened. Use PNG or JPEG.`, switchModel: false } : null;
}
